import React, { useState, useEffect } from 'react';
import { notification } from '../../hocs/notification';
import axios from 'axios';
import { mainurl } from '../../../Mainurl';

import { 
    Card, CardHeader, CardBody, Button, Row, Col, Form, FormGroup, 
    Label, Input, Alert, Spinner, Badge, Progress 
} from 'reactstrap';
import { 
    FaCertificate, FaArrowLeft, FaUpload, FaCheckCircle, 
    FaClock, FaExclamationTriangle, FaSave, FaList,
    FaCalendarAlt, FaInfoCircle, FaCalendarCheck,
    FaFileAlt, FaIdCard
} from 'react-icons/fa';

const AddCertificate = ({ back, ...props }) => {
    const [certificateType, setCertificateType] = useState('');
    const [certificateTypeOpt, setCertificateTypeOpt] = useState('');
    const [certificateNo, setCertificateNo] = useState('');
    const [certificateDesc, setCertificateDesc] = useState('');
    const [issueDate, setIssueDate] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [certificateFile, setCertificateFile] = useState('');

    // Enhanced state for better UX
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [selectedFileName, setSelectedFileName] = useState('');
    const [certificateStatus, setCertificateStatus] = useState('');

    useEffect(() => {
        get_certificate_type_list();
    }, []);

    // Calculate certificate status based on dates
    useEffect(() => {
        if (issueDate && expiryDate) {
            const today = new Date();
            const expiry = new Date(expiryDate);
            const issue = new Date(issueDate);
            
            if (issue > today) {
                setCertificateStatus('future');
            } else if (expiry < today) {
                setCertificateStatus('expired');
            } else {
                // Check if expiring within 30 days
                const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
                if (daysUntilExpiry <= 30) {
                    setCertificateStatus('expiring');
                } else {
                    setCertificateStatus('valid');
                }
            }
        } else {
            setCertificateStatus('');
        }
    }, [issueDate, expiryDate]);

    // Validation function
    const validateForm = () => {
        const errors = {};
        if (!certificateType || certificateType === '0') errors.certificateType = 'Please select a certificate type';
        if (!certificateNo.trim()) errors.certificateNo = 'Please enter certificate number';
        if (!certificateDesc.trim()) errors.certificateDesc = 'Please enter certificate description';
        if (!issueDate) errors.issueDate = 'Please select issue date';
        if (!expiryDate) errors.expiryDate = 'Please select expiry date';
        
        // Date validation
        if (issueDate && expiryDate) {
            const issue = new Date(issueDate);
            const expiry = new Date(expiryDate);
            if (expiry <= issue) {
                errors.expiryDate = 'Expiry date must be after issue date';
            }
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const get_certificate_type_list = () => {
        setLoading(true);
        axios({
            method: 'post',
            url: mainurl + 'Master/certificate_type_list',
            headers: { 'Content-Type': 'application/JSON', }
        })
            .then(function (response) {
                let list = response.data.map((row, index) => {
                    return(
                        <option key={index} value={row.certificate_type_id}> {row.certificate_name} </option>
                    )
                });

                setCertificateTypeOpt(list);
                setLoading(false);
            })
            .catch(error => {
                setLoading(false);
                let notify = notification({ message: "Failed to load certificate types! Try Again", type: 'error' });
                notify();
            });
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFileName(file.name);
            setCertificateFile(e.target.value);
        } else {
            setSelectedFileName('');
            setCertificateFile('');
        }
    };

    const add_certificate_handler = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitLoading(true);

        let postData = [{
            "org_id": sessionStorage.getItem('org_id'),
            "certificate_type_id": certificateType,
            "certificate_no": certificateNo,
            "certificate_desc": certificateDesc,
            "issue_date": issueDate,
            "expiry_date": expiryDate
        }]

        axios({
            method: 'post',
            url: mainurl + 'Master/add_certificate',
            data: postData,
            headers: { 'Content-Type': 'application/JSON', }
        })
        .then(function (response) {
            let res = response.data[0].retcode;
            if(res === "101"){
                setSuccessMessage("Certificate added successfully!");
                setTimeout(() => {
                    let notify = notification({ message: "Certificate added Successfully.", type: 'info', time:true, position:'center' });
                    notify();
                    back();
                }, 1500);
            }
            setSubmitLoading(false);
        })
        .catch(error => {
            setSubmitLoading(false);
            let notify = notification({ message: "Submit failed! Try Again", type: 'error', time:true });
            notify();
        });
    }

    const getStatusBadge = () => {
        switch(certificateStatus) {
            case 'valid':
                return <Badge color="success"><FaCheckCircle className="me-1" />Valid</Badge>;
            case 'expiring':
                return <Badge color="warning"><FaClock className="me-1" />Expiring Soon</Badge>;
            case 'expired':
                return <Badge color="danger"><FaExclamationTriangle className="me-1" />Expired</Badge>;
            case 'future':
                return <Badge color="info"><FaCalendarAlt className="me-1" />Future</Badge>;
            default:
                return null;
        }
    };

    const getDaysUntilExpiry = () => {
        if (!expiryDate) return null;
        const today = new Date();
        const expiry = new Date(expiryDate);
        const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
        return days;
    };

    return (
        <>
            {/* Success Alert */}
            {successMessage && (
                <Alert color="success" className="d-flex align-items-center mb-3">
                    <FaCheckCircle className="me-2" />
                    {successMessage}
                </Alert>
            )}

            {/* Main Form Card */}
            <Card className="shadow-sm border-primary">
                <CardHeader className='bg-primary text-white d-flex justify-content-between align-items-center'>
                    <div className="d-flex align-items-center">
                        <FaCertificate className="me-2" />
                        <h5 className="mb-0">Add Certificate</h5>
                    </div>
                    <Button 
                        color="light" 
                        size="sm" 
                        onClick={back}
                        className="d-flex align-items-center"
                    > 
                        <FaArrowLeft className="me-1" /> Back
                    </Button>
                </CardHeader>
                <CardBody className="p-4">
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <p className="mt-2 text-primary">Loading certificate types...</p>
                        </div>
                    ) : (
                        <Form onSubmit={add_certificate_handler}>
                            <Row>
                                {/* Certificate Type */}
                                <Col lg={6} className="mb-4">
                                    <FormGroup>
                                        <Label for="certificateType" className="fw-bold text-primary mb-2">
                                            <FaList className="me-2" />
                                            Certificate Type <span className="text-danger">*</span>
                                        </Label>
                                        <Input 
                                            id="certificateType" 
                                            bsSize="sm" 
                                            type="select" 
                                            value={certificateType} 
                                            onChange={(e) => {
                                                setCertificateType(e.target.value);
                                                if (formErrors.certificateType) {
                                                    setFormErrors(prev => ({ ...prev, certificateType: '' }));
                                                }
                                            }}
                                            invalid={!!formErrors.certificateType}
                                            className="border-primary"
                                        >
                                            <option value="0">-- Select Certificate Type --</option>
                                            {certificateTypeOpt}
                                        </Input>
                                        {formErrors.certificateType && (
                                            <small className="text-danger">{formErrors.certificateType}</small>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Certificate Number */}
                                <Col lg={6} className="mb-4">
                                    <FormGroup>
                                        <Label for="certificateNo" className="fw-bold text-primary mb-2">
                                            <FaIdCard className="me-2" />
                                            Certificate Number <span className="text-danger">*</span>
                                        </Label>
                                        <Input 
                                            id="certificateNo" 
                                            type='text' 
                                            bsSize="sm" 
                                            value={certificateNo} 
                                            onChange={(e) => {
                                                setCertificateNo(e.target.value);
                                                if (formErrors.certificateNo) {
                                                    setFormErrors(prev => ({ ...prev, certificateNo: '' }));
                                                }
                                            }}
                                            placeholder="Enter certificate number"
                                            invalid={!!formErrors.certificateNo}
                                            className="border-primary"
                                        />
                                        {formErrors.certificateNo && (
                                            <small className="text-danger">{formErrors.certificateNo}</small>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Certificate Description */}
                                <Col lg={12} className="mb-4">
                                    <FormGroup>
                                        <Label for="certificateDesc" className="fw-bold text-primary mb-2">
                                            <FaInfoCircle className="me-2" />
                                            Certificate Description <span className="text-danger">*</span>
                                        </Label>
                                        <Input 
                                            id="certificateDesc" 
                                            type='textarea' 
                                            bsSize="sm" 
                                            rows="3"
                                            value={certificateDesc} 
                                            onChange={(e) => {
                                                setCertificateDesc(e.target.value);
                                                if (formErrors.certificateDesc) {
                                                    setFormErrors(prev => ({ ...prev, certificateDesc: '' }));
                                                }
                                            }}
                                            placeholder="Enter detailed certificate description"
                                            invalid={!!formErrors.certificateDesc}
                                            className="border-primary"
                                        />
                                        {formErrors.certificateDesc && (
                                            <small className="text-danger">{formErrors.certificateDesc}</small>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Issue Date */}
                                <Col lg={6} className="mb-4">
                                    <FormGroup>
                                        <Label for="issueDate" className="fw-bold text-primary mb-2">
                                            <FaCalendarAlt className="me-2" />
                                            Issue Date <span className="text-danger">*</span>
                                        </Label>
                                        <Input 
                                            id="issueDate" 
                                            type='date' 
                                            bsSize="sm" 
                                            value={issueDate} 
                                            onChange={(e) => {
                                                setIssueDate(e.target.value);
                                                if (formErrors.issueDate) {
                                                    setFormErrors(prev => ({ ...prev, issueDate: '' }));
                                                }
                                            }}
                                            invalid={!!formErrors.issueDate}
                                            className="border-primary"
                                            max={new Date().toISOString().split('T')[0]}
                                        />
                                        {formErrors.issueDate && (
                                            <small className="text-danger">{formErrors.issueDate}</small>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Expiry Date */}
                                <Col lg={6} className="mb-4">
                                    <FormGroup>
                                        <Label for="expiryDate" className="fw-bold text-primary mb-2">
                                            <FaCalendarCheck className="me-2" />
                                            Expiry Date <span className="text-danger">*</span>
                                        </Label>
                                        <div className="d-flex align-items-center">
                                            <Input 
                                                id="expiryDate" 
                                                type='date' 
                                                bsSize="sm" 
                                                value={expiryDate} 
                                                onChange={(e) => {
                                                    setExpiryDate(e.target.value);
                                                    if (formErrors.expiryDate) {
                                                        setFormErrors(prev => ({ ...prev, expiryDate: '' }));
                                                    }
                                                }}
                                                invalid={!!formErrors.expiryDate}
                                                className="border-primary me-2"
                                                min={issueDate || undefined}
                                            />
                                            {getStatusBadge()}
                                        </div>
                                        {formErrors.expiryDate && (
                                            <small className="text-danger">{formErrors.expiryDate}</small>
                                        )}
                                        {certificateStatus === 'expiring' && getDaysUntilExpiry() > 0 && (
                                            <small className="text-warning">
                                                <FaClock className="me-1" />
                                                Expires in {getDaysUntilExpiry()} days
                                            </small>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* File Upload */}
                                <Col lg={12} className="mb-4">
                                    <FormGroup>
                                        <Label for="certificateFile" className="fw-bold text-primary mb-2">
                                            <FaUpload className="me-2" />
                                            Attach Certificate Document (Optional)
                                        </Label>
                                        <div className="border border-primary rounded p-3 bg-primary bg-opacity-10">
                                            <Input 
                                                id="certificateFile" 
                                                bsSize="sm" 
                                                type="file" 
                                                onChange={handleFileChange}
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                className="border-0"
                                            />
                                            {selectedFileName && (
                                                <div className="mt-2 text-success">
                                                    <FaCheckCircle className="me-1" />
                                                    Selected: {selectedFileName}
                                                </div>
                                            )}
                                            <small className="text-muted">
                                                Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max size: 5MB)
                                            </small>
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>

                            {/* Certificate Summary */}
                            {(certificateNo || issueDate || expiryDate) && (
                                <Card className="bg-primary bg-opacity-10 border-primary mb-4">
                                    <CardBody className="py-3">
                                        <h6 className="text-primary mb-2">
                                            <FaCertificate className="me-2" />
                                            Certificate Summary
                                        </h6>
                                        <Row>
                                            {certificateNo && (
                                                <Col md={4} className="mb-2">
                                                    <strong>Certificate No:</strong> 
                                                    <span className="ms-2 text-primary">{certificateNo}</span>
                                                </Col>
                                            )}
                                            {issueDate && (
                                                <Col md={4} className="mb-2">
                                                    <strong>Issue Date:</strong> 
                                                    <span className="ms-2">{new Date(issueDate).toLocaleDateString()}</span>
                                                </Col>
                                            )}
                                            {expiryDate && (
                                                <Col md={4} className="mb-2">
                                                    <strong>Expiry Date:</strong> 
                                                    <span className="ms-2">{new Date(expiryDate).toLocaleDateString()}</span>
                                                </Col>
                                            )}
                                            {certificateStatus && (
                                                <Col md={4} className="mb-2">
                                                    <strong>Status:</strong> 
                                                    <span className="ms-2">{getStatusBadge()}</span>
                                                </Col>
                                            )}
                                            {selectedFileName && (
                                                <Col md={4} className="mb-2">
                                                    <strong>Document:</strong> 
                                                    <span className="ms-2 text-success">
                                                        <FaFileAlt className="me-1" />
                                                        Attached
                                                    </span>
                                                </Col>
                                            )}
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}

                            {/* Expiry Warning */}
                            {certificateStatus === 'expired' && (
                                <Alert color="danger" className="mb-4">
                                    <FaExclamationTriangle className="me-2" />
                                    <strong>Warning:</strong> This certificate has already expired. Please renew it as soon as possible.
                                </Alert>
                            )}

                            {certificateStatus === 'expiring' && (
                                <Alert color="warning" className="mb-4">
                                    <FaClock className="me-2" />
                                    <strong>Notice:</strong> This certificate will expire in {getDaysUntilExpiry()} days. Consider renewing it soon.
                                </Alert>
                            )}

                            {/* Submit Section */}
                            <hr className="border-primary" />
                            <Row>
                                <Col md={12} className="text-end">
                                    <Button 
                                        color='primary' 
                                        size="sm" 
                                        disabled={submitLoading}
                                        className="px-4 py-2"
                                        type="submit"
                                    > 
                                        {submitLoading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Adding Certificate...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="me-2" />
                                                Add Certificate
                                            </>
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    )}
                </CardBody>
            </Card>        
        </>
    )
}

export default AddCertificate;